import { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { housingAPI, uploadAPI, getShareUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, MapPin, Home, Bed, Bath, DollarSign, Plus, Loader2,
  MessageCircle, Heart, X, Calendar, Camera, Video, Building2,
  Key, Landmark, Eye, Megaphone, Share2, Copy, Check
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useCommunity } from '../contexts/CommunityContext';
import LocationAutocomplete from '../components/LocationAutocomplete';
import usePageTitle from '../hooks/usePageTitle';
import ShareMenu from '../components/ShareMenu';

const RENTAL_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'condo', label: 'Condo' },
  { value: 'house', label: 'House' },
  { value: 'room', label: 'Room' },
  { value: 'studio', label: 'Studio' },
  { value: 'townhouse', label: 'Townhouse' },
];

const SALE_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'house', label: 'House' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'land', label: 'Land' },
  { value: 'commercial', label: 'Commercial' },
];

const Housing = ({ mode = 'rental' }) => {
  const pageTitle = mode === 'sale' ? 'Real Estate' : 'Property Rentals';
  usePageTitle(pageTitle);
  const { user } = useAuth();
  const { institutionFilter } = useCommunity();

  const activeTab = mode;
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [propertyType, setPropertyType] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [listingMode, setListingMode] = useState('listing'); // listing or wanted

  const [newListing, setNewListing] = useState({
    title: '', description: '', property_type: 'apartment', listing_category: 'rental',
    price_type: 'monthly', bedrooms: 1, bathrooms: 1, price: '', location: '',
    address: '', amenities: [], photos: [], available_date: '', video_url: ''
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const isRentals = activeTab === 'rental';
  const propertyTypes = isRentals ? RENTAL_TYPES : SALE_TYPES;

  useEffect(() => {
    setPropertyType('all');
    fetchListings();
  }, [mode, institutionFilter.institution]);

  useEffect(() => { fetchListings(); }, [propertyType]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = { ...institutionFilter, listing_category: activeTab };
      if (search) params.location = search;
      if (propertyType !== 'all') params.property_type = propertyType;
      const res = await housingAPI.list(params);
      // Also fetch wanted listings for this category
      const wantedParams = { ...institutionFilter, listing_category: `wanted_${activeTab}` };
      if (search) wantedParams.location = search;
      const wantedRes = await housingAPI.list(wantedParams);
      const allListings = [...(Array.isArray(res.data) ? res.data : []), ...(Array.isArray(wantedRes.data) ? wantedRes.data : [])];
      allListings.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setListings(allListings);
    } catch (error) {
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Video must be under 100MB'); return; }
    setVideoFile(file);
    setVideoPreviewUrl(URL.createObjectURL(file));
  };

  const resetForm = () => {
    const defaultType = isRentals ? 'apartment' : 'house';
    setNewListing({
      title: '', description: '', property_type: defaultType,
      listing_category: activeTab, price_type: isRentals ? 'monthly' : 'total',
      bedrooms: 1, bathrooms: 1, price: '', location: '', address: '',
      amenities: [], photos: [], available_date: '', video_url: ''
    });
    setPhotoPreview(null); setPhotoFile(null);
    setVideoFile(null); setVideoPreviewUrl(null);
    setListingMode('listing');
  };

  const openCreateDialog = (mode = 'listing') => {
    resetForm();
    setListingMode(mode);
    if (mode === 'wanted') {
      setNewListing(prev => ({
        ...prev,
        listing_category: `wanted_${activeTab}`,
        price_type: isRentals ? 'monthly' : 'total',
      }));
    } else {
      setNewListing(prev => ({
        ...prev,
        listing_category: activeTab,
        price_type: isRentals ? 'monthly' : 'total',
      }));
    }
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!newListing.title || !newListing.price) { toast.error('Title and price/budget are required'); return; }
    setCreating(true);
    try {
      let photos = newListing.photos;
      let video_url = newListing.video_url;

      if (photoFile) {
        setUploadingPhoto(true);
        try {
          const uploadRes = await uploadAPI.postImage(photoFile);
          photos = [uploadRes.data.url, ...photos];
        } catch { toast.error('Photo upload failed'); setCreating(false); setUploadingPhoto(false); return; }
        setUploadingPhoto(false);
      }
      if (videoFile) {
        setUploadingVideo(true);
        try {
          const uploadRes = await uploadAPI.postImage(videoFile);
          video_url = uploadRes.data.url;
        } catch { toast.error('Video upload failed'); setCreating(false); setUploadingVideo(false); return; }
        setUploadingVideo(false);
      }

      const res = await housingAPI.create({
        ...newListing,
        price: parseFloat(newListing.price),
        photos,
        video_url,
      });
      setListings([res.data, ...listings]);
      setShowCreate(false);
      resetForm();
      toast.success(listingMode === 'wanted' ? 'Wanted post created!' : 'Listing posted!');
    } catch (error) { toast.error('Failed to create listing'); }
    finally { setCreating(false); }
  };

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isWanted = (listing) => listing.listing_category?.startsWith('wanted');

  const formatPrice = (listing) => {
    const price = listing.price?.toLocaleString();
    if (isWanted(listing)) return `$${price} budget`;
    if (listing.price_type === 'total' || listing.listing_category === 'sale') return `$${price}`;
    return `$${price}/mo`;
  };

  // Separate listings and wanted
  const regularListings = listings.filter(l => !isWanted(l));
  const wantedListings = listings.filter(l => isWanted(l));

  const renderCard = (listing) => {
    const wanted = isWanted(listing);
    return (
      <div key={listing.housing_id}
        className={`bg-white rounded-2xl border shadow-ambient hover:shadow-hover transition-shadow overflow-hidden cursor-pointer ${wanted ? 'border-amber-200' : 'border-slate-100'}`}
        onClick={() => setSelected(listing)}>
        <div className={`relative h-48 ${wanted ? 'bg-gradient-to-br from-amber-50 to-yellow-100' : 'bg-gradient-to-br from-emerald-50 to-teal-100'}`}>
          {listing.photos?.[0] ? (
            <img src={listing.photos[0]} alt={listing.title} className="w-full h-full object-cover" />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-2">
              {wanted ? <Eye className="w-12 h-12 text-amber-300" /> : <Home className="w-12 h-12 text-emerald-300" />}
              {wanted && <span className="text-sm font-medium text-amber-400">Looking to {isRentals ? 'rent' : 'buy'}</span>}
            </div>
          )}
          <div className="absolute top-3 left-3 flex gap-1.5">
            {wanted && <Badge className="bg-amber-500 text-white border-0">Wanted</Badge>}
            <Badge className="bg-white/90 text-dark-slate">{listing.property_type}</Badge>
          </div>
          <div className="absolute top-3 right-3 flex gap-1.5">
            <button onClick={(e) => { e.stopPropagation(); const url = getShareUrl('housing', listing.housing_id); if (navigator.share) { navigator.share({ title: listing.title, url }); } else { navigator.clipboard.writeText(url); toast.success('Link copied!'); } }}
              className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors">
              <Share2 className="w-4 h-4 text-slate-gray" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); toggleFavorite(listing.housing_id); }}
              className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
              <Heart className={`w-4 h-4 ${favorites.has(listing.housing_id) ? 'text-rose-500 fill-current' : 'text-slate-gray'}`} />
            </button>
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-heading font-bold text-lg text-dark-slate mb-1 line-clamp-1">{listing.title}</h3>
          <p className={`text-2xl font-bold mb-2 ${wanted ? 'text-amber-600' : 'text-emerald-600'}`}>
            {formatPrice(listing)}
          </p>
          {listing.location && <p className="text-sm text-slate-gray flex items-center mb-2"><MapPin className="w-3 h-3 mr-1" />{listing.location}</p>}
          <div className="flex items-center gap-3 text-sm text-slate-gray">
            {listing.bedrooms != null && <span className="flex items-center"><Bed className="w-4 h-4 mr-1" />{listing.bedrooms} bed</span>}
            {listing.bathrooms != null && <span className="flex items-center"><Bath className="w-4 h-4 mr-1" />{listing.bathrooms} bath</span>}
          </div>
          {listing.institution && <Badge variant="outline" className="mt-2 text-[10px]">{listing.institution}</Badge>}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-dark-slate">{pageTitle}</h1>
          </div>
        </div>

        {/* Page description */}
        {isRentals ? (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-5 mb-6 border border-emerald-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-dark-slate">Property Rentals</h2>
                <p className="text-sm text-slate-gray mt-0.5">
                  Off-campus housing, vacation rentals, and properties available for rent from the alumni network.
                  Looking for a place? Post a "Wanted" listing to let others know what you need.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 mb-6 border border-blue-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Landmark className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-dark-slate">Real Estate</h2>
                <p className="text-sm text-slate-gray mt-0.5">
                  Properties for sale by alumni — homes, condos, land, and more. Buy and sell real estate within the
                  SpelHouse community. Post a "Wanted" listing if you're searching for a specific type of property.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2 mb-6">
          <Button onClick={() => openCreateDialog('listing')} className="bg-dark-slate text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" />{isRentals ? 'List a Rental' : 'List Property for Sale'}
          </Button>
          <Button onClick={() => openCreateDialog('wanted')} variant="outline" className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50">
            <Megaphone className="w-4 h-4 mr-2" />Post Wanted
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1">
            <LocationAutocomplete
              value={search}
              onChange={setSearch}
              onSelect={(loc) => { setSearch(loc); setTimeout(fetchListings, 100); }}
              placeholder="Search by city or zip code..."
              className="h-11 rounded-xl bg-white border-slate-200"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {propertyTypes.map(t => (
              <button key={t.value} onClick={() => setPropertyType(t.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  propertyType === t.value ? 'bg-dark-slate text-white' : 'bg-white text-slate-gray border border-slate-200 hover:bg-slate-50'
                }`}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Listings */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-gray" /></div>
        ) : listings.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
            <Home className="w-12 h-12 text-slate-gray mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">No listings yet</h3>
            <p className="text-slate-gray">
              {isRentals ? 'Be the first to post a rental property!' : 'Be the first to list a property for sale!'}
            </p>
          </div>
        ) : (
          <>
            {/* Regular listings */}
            {regularListings.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {regularListings.map(renderCard)}
              </div>
            )}

            {/* Wanted section */}
            {wantedListings.length > 0 && (
              <>
                <div className="flex items-center gap-3 mb-4 mt-2">
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-amber-500" />
                    <h3 className="font-heading font-bold text-lg text-dark-slate">
                      {isRentals ? 'Looking to Rent' : 'Looking to Buy'}
                    </h3>
                  </div>
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-gray">{wantedListings.length} {wantedListings.length === 1 ? 'post' : 'posts'}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {wantedListings.map(renderCard)}
                </div>
              </>
            )}
          </>
        )}

        {/* Detail Modal */}
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
            {selected && (
              <>
                {selected.photos?.[0] && <img src={selected.photos[0]} alt={selected.title} className="w-full h-48 object-cover rounded-xl mb-4" />}
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">
                    {isWanted(selected) && <Badge className="bg-amber-500 text-white border-0 mr-2 text-xs align-middle">Wanted</Badge>}
                    {selected.title}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-3">
                  <p className={`text-3xl font-bold ${isWanted(selected) ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {formatPrice(selected)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{selected.property_type}</Badge>
                    {selected.bedrooms != null && <Badge variant="secondary"><Bed className="w-3 h-3 mr-1" />{selected.bedrooms} bed</Badge>}
                    {selected.bathrooms != null && <Badge variant="secondary"><Bath className="w-3 h-3 mr-1" />{selected.bathrooms} bath</Badge>}
                    {selected.listing_category === 'sale' && <Badge variant="secondary" className="bg-blue-50 text-blue-700">For Sale</Badge>}
                    {selected.listing_category === 'rental' && <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">For Rent</Badge>}
                  </div>
                  {selected.location && <p className="text-sm text-slate-gray flex items-center"><MapPin className="w-4 h-4 mr-2" />{selected.location}</p>}
                  {selected.address && <p className="text-sm text-slate-gray">{selected.address}</p>}
                  {selected.description && <div><h4 className="font-semibold text-dark-slate mb-1">Description</h4><p className="text-slate-gray text-sm whitespace-pre-wrap">{selected.description}</p></div>}
                  {selected.amenities?.length > 0 && (
                    <div><h4 className="font-semibold text-dark-slate mb-2">Amenities</h4>
                      <div className="flex flex-wrap gap-2">{selected.amenities.map((a, i) => <Badge key={i} variant="secondary" className="bg-slate-100 text-xs">{a}</Badge>)}</div>
                    </div>
                  )}
                  {selected.video_url && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2 flex items-center gap-1"><Video className="w-4 h-4" />Video Tour</h4>
                      <video src={selected.video_url} controls className="w-full rounded-xl max-h-56 bg-black" />
                    </div>
                  )}
                  {selected.posted_by_name && (
                    <div className="bg-mint-fresh/20 rounded-xl p-3 text-sm">
                      Posted by <span className="font-semibold text-dark-slate">{selected.posted_by_name}</span>
                      {selected.institution && <span className="text-slate-gray"> from {selected.institution}</span>}
                    </div>
                  )}
                  <div className="flex gap-3 pt-4 border-t border-slate-100">
                    <Button className="flex-1 h-11 bg-dark-slate text-white rounded-full" onClick={() => toast.info('Messaging coming soon!')}>
                      <MessageCircle className="w-4 h-4 mr-2" />Contact
                    </Button>
                    <Button variant="outline" className="h-11 rounded-full px-4" onClick={() => toggleFavorite(selected.housing_id)}>
                      <Heart className={`w-4 h-4 ${favorites.has(selected.housing_id) ? 'fill-current text-rose-500' : ''}`} />
                    </Button>
                  </div>
                  <div className="pt-3">
                    <p className="text-xs font-medium text-slate-gray mb-2">Share this listing</p>
                    <ShareMenu url={getShareUrl('housing', selected.housing_id)} title={selected.title} />
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {listingMode === 'wanted'
                  ? (isRentals ? 'Looking to Rent' : 'Looking to Buy')
                  : (isRentals ? 'List a Rental Property' : 'List Property for Sale')}
              </DialogTitle>
            </DialogHeader>

            {listingMode === 'wanted' && (
              <div className="bg-amber-50 rounded-xl p-3 text-sm text-amber-800 border border-amber-200">
                <strong>Wanted post:</strong> Let the community know what you're looking for. Include your budget, preferred location, and any requirements.
              </div>
            )}

            <div className="space-y-4 mt-4">
              <div>
                <Label>{listingMode === 'wanted' ? 'What are you looking for? *' : 'Title *'}</Label>
                <Input value={newListing.title}
                  onChange={(e) => setNewListing(p => ({ ...p, title: e.target.value }))}
                  placeholder={listingMode === 'wanted'
                    ? (isRentals ? 'e.g. Looking for 2BR near Midtown' : 'e.g. Looking for a starter home in ATL')
                    : (isRentals ? 'e.g. 2BR Condo - Midtown' : 'e.g. 3BR Ranch - 116 Peyton Road')}
                  className="mt-1" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newListing.description}
                  onChange={(e) => setNewListing(p => ({ ...p, description: e.target.value }))}
                  rows={3} className="mt-1"
                  placeholder={listingMode === 'wanted' ? 'Describe what you\'re looking for — size, neighborhood, must-haves, move-in date, etc.' : ''} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{listingMode === 'wanted' ? 'Budget *' : (isRentals ? 'Rent ($/month) *' : 'Sale Price ($) *')}</Label>
                  <Input type="number" value={newListing.price}
                    onChange={(e) => setNewListing(p => ({ ...p, price: e.target.value }))}
                    placeholder={isRentals ? '1850' : '449000'}
                    className="mt-1" />
                </div>
                <div>
                  <Label>Property Type</Label>
                  <Select value={newListing.property_type} onValueChange={(v) => setNewListing(p => ({ ...p, property_type: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(isRentals ? RENTAL_TYPES : SALE_TYPES).filter(t => t.value !== 'all').map(t =>
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Bedrooms</Label><Input type="number" value={newListing.bedrooms} onChange={(e) => setNewListing(p => ({ ...p, bedrooms: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
                <div><Label>Bathrooms</Label><Input type="number" value={newListing.bathrooms} onChange={(e) => setNewListing(p => ({ ...p, bathrooms: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
              </div>
              <div>
                <Label>{listingMode === 'wanted' ? 'Preferred Location' : 'Location'}</Label>
                <LocationAutocomplete value={newListing.location} onChange={(val) => setNewListing(p => ({ ...p, location: val }))} placeholder="Atlanta, GA" className="mt-1" />
              </div>

              {/* Photo Upload — only for actual listings */}
              {listingMode === 'listing' && (
                <>
                  <div>
                    <Label>Photo</Label>
                    <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                    {photoPreview ? (
                      <div className="relative mt-1">
                        <img src={photoPreview} alt="Preview" className="w-full h-40 object-cover rounded-xl border border-slate-200" />
                        <button type="button" onClick={() => { setPhotoPreview(null); setPhotoFile(null); if (photoInputRef.current) photoInputRef.current.value = ''; }}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => photoInputRef.current?.click()}
                        className="mt-1 w-full h-32 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center gap-2 text-slate-gray hover:border-slate-400 hover:bg-slate-50 transition-colors">
                        <Camera className="w-7 h-7" /><span className="text-sm">Click to add a photo</span>
                      </button>
                    )}
                  </div>

                  <div>
                    <Label>Video Tour <span className="text-slate-gray text-xs font-normal">(mp4, mov, webm — max 100MB)</span></Label>
                    <input ref={videoInputRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={handleVideoChange} />
                    {videoPreviewUrl ? (
                      <div className="relative mt-1">
                        <video src={videoPreviewUrl} controls className="w-full rounded-xl border border-slate-200 max-h-44 bg-black" />
                        <button type="button" onClick={() => { setVideoFile(null); setVideoPreviewUrl(null); if (videoInputRef.current) videoInputRef.current.value = ''; }}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => videoInputRef.current?.click()}
                        className="mt-1 w-full h-32 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center gap-2 text-slate-gray hover:border-slate-400 hover:bg-slate-50 transition-colors">
                        <Video className="w-7 h-7" /><span className="text-sm">Click to add a video tour</span>
                      </button>
                    )}
                  </div>
                </>
              )}

              <Button onClick={handleCreate} disabled={creating || uploadingPhoto || uploadingVideo} className="w-full h-11 bg-dark-slate text-white rounded-full">
                {creating || uploadingPhoto || uploadingVideo ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{uploadingPhoto ? 'Uploading photo...' : uploadingVideo ? 'Uploading video...' : 'Posting...'}</>
                ) : (listingMode === 'wanted' ? 'Post Wanted Listing' : (isRentals ? 'List Rental' : 'List Property'))}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Housing;
